import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  searchTrombinos,
  getTrombinoById,
  createTrombino,
  updateTrombino,
  deleteTrombino,
  toggleTrombinoVisible,
  toggleTrombinoElected,
} from "../../../../services/trombinosServices";

import { getAllServicesMunicipaux } from "../../../../services/servicesMunicipauxServices";

import { TROMBINOS_INITIAL_FORM } from "../data/trombinosAdminData";

import {
  buildServiceOptionsFromRows,
  buildTrombinosSearchParams,
  getTrombinosStats,
  normalizeTrombinoForm,
  validateTrombinoForm,
} from "../utils/trombinosAdminUtils";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

export default function useTrombinosAdmin() {
  const mountedRef = useRef(true);
  const noticeTimeoutRef = useRef(null);

  const [q, setQ] = useState("");
  const [electedFilter, setElectedFilter] = useState("all");
  const [visibleFilter, setVisibleFilter] = useState("visible");
  const [serviceFilter, setServiceFilter] = useState("all");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [servicesOptions, setServicesOptions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionId, setActionId] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [form, setForm] = useState({ ...TROMBINOS_INITIAL_FORM });
  const [fieldErrors, setFieldErrors] = useState({});

  const [previewItem, setPreviewItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / limit) || 1, 1);
  }, [total, limit]);

  const stats = useMemo(() => {
    return getTrombinosStats(items, total);
  }, [items, total]);

  const showNotice = useCallback((message) => {
    setNotice(message);

    if (noticeTimeoutRef.current) {
      window.clearTimeout(noticeTimeoutRef.current);
    }

    noticeTimeoutRef.current = window.setTimeout(() => {
      setNotice("");
    }, 3500);
  }, []);

  const loadServicesOptions = useCallback(async () => {
    try {
      const rows = await getAllServicesMunicipaux();
      const list = Array.isArray(rows)
        ? rows
            .filter((service) => service?.id)
            .map((service) => ({
              id: service.id,
              label: service.nom_service || `Service #${service.id}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label, "fr"))
        : [];

      if (!mountedRef.current) return;

      setServicesOptions(list);
    } catch (serviceError) {
      console.error("Erreur chargement options services :", serviceError);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = buildTrombinosSearchParams({
        q,
        electedFilter,
        visibleFilter,
        serviceFilter,
        page,
        limit,
      });

      const res = await searchTrombinos(params);

      if (!mountedRef.current) return;

      const nextItems = Array.isArray(res?.items) ? res.items : [];

      setItems(nextItems);
      setTotal(Number(res?.total || 0));

      if (!servicesOptions.length) {
        const inferred = buildServiceOptionsFromRows(nextItems);
        if (inferred.length) {
          setServicesOptions(inferred);
        }
      }
    } catch (fetchError) {
      console.error("Erreur chargement trombinoscope :", fetchError);

      if (!mountedRef.current) return;

      setItems([]);
      setTotal(0);
      setError("Impossible de charger le trombinoscope pour le moment.");
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [
    q,
    electedFilter,
    visibleFilter,
    serviceFilter,
    page,
    limit,
    servicesOptions.length,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    loadServicesOptions();

    return () => {
      mountedRef.current = false;

      if (noticeTimeoutRef.current) {
        window.clearTimeout(noticeTimeoutRef.current);
      }
    };
  }, [loadServicesOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmitSearch = useCallback(
    (event) => {
      event?.preventDefault?.();

      if (page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    },
    [fetchData, page],
  );

  const changeElectedFilter = useCallback((value) => {
    setElectedFilter(value);
    setPage(1);
  }, []);

  const changeVisibleFilter = useCallback((value) => {
    setVisibleFilter(value);
    setPage(1);
  }, []);

  const changeServiceFilter = useCallback((value) => {
    setServiceFilter(value);
    setPage(1);
  }, []);

  const changeLimit = useCallback((value) => {
    setLimit(Number(value) || 24);
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setQ("");
    setElectedFilter("all");
    setVisibleFilter("visible");
    setServiceFilter("all");
    setLimit(24);
    setPage(1);
  }, []);

  const openCreate = useCallback(() => {
    setCurrentId(null);
    setForm({ ...TROMBINOS_INITIAL_FORM });
    setFieldErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback(async (id) => {
    if (!id) return;

    try {
      setSaving(true);
      setError("");

      const row = await getTrombinoById(id);

      if (!row) {
        setError("Profil introuvable.");
        return;
      }

      setCurrentId(id);
      setForm({
        nom: row.nom || "",
        prenom: row.prenom || "",
        fonction: row.fonction || "",
        role_type: row.role_type || "",
        groupe: row.groupe || "",
        service_id:
          row.service_id !== null && row.service_id !== undefined
            ? String(row.service_id)
            : "",
        mail_pro: row.mail_pro || "",
        tel_pro: row.tel_pro || "",
        photo_url: row.photo_url || "",
        presentation_courte: row.presentation_courte || "",
        bio_html: row.bio_html || "",
        mandat_debut: row.mandat_debut || "",
        mandat_fin: row.mandat_fin || "",
        ordre_affichage:
          row.ordre_affichage !== null && row.ordre_affichage !== undefined
            ? Number(row.ordre_affichage) || 0
            : 0,
        is_elected: !!row.is_elected,
        is_visible: !!row.is_visible,
      });
      setFieldErrors({});
      setFormOpen(true);
    } catch (editError) {
      console.error("Erreur ouverture profil trombinoscope :", editError);
      setError("Impossible d’ouvrir ce profil.");
    } finally {
      setSaving(false);
    }
  }, []);

  const openEditFromPreview = useCallback(async () => {
    if (!previewItem?.id) return;

    const id = previewItem.id;
    setPreviewItem(null);
    await openEdit(id);
  }, [openEdit, previewItem]);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setCurrentId(null);
    setForm({ ...TROMBINOS_INITIAL_FORM });
    setFieldErrors({});
    setMediaPickerOpen(false);
  }, []);

  const updateFormField = useCallback((field, value) => {
    setForm((previous) => {
      const next = {
        ...previous,
        [field]: value,
      };

      if (field === "is_elected") {
        next.role_type = value
          ? "elu"
          : previous.role_type === "elu"
            ? "personnel"
            : previous.role_type;
      }

      if (field === "role_type") {
        if (value === "elu") {
          next.is_elected = true;
        } else if (value === "personnel") {
          next.is_elected = false;
        }
      }

      return next;
    });

    setFieldErrors((previous) => {
      if (!previous[field]) return previous;

      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  const onSave = useCallback(async () => {
    const errors = validateTrombinoForm(form);

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) return;

    try {
      setSaving(true);
      setError("");

      const payload = normalizeTrombinoForm(form);

      if (payload.photo_url) {
        payload.photo_url = toAbsolutePublicUrl(payload.photo_url);
      }

      if (currentId) {
        await updateTrombino(currentId, payload);
        showNotice("Profil modifié avec succès.");
      } else {
        await createTrombino(payload);
        showNotice("Profil créé avec succès.");
      }

      closeForm();
      await fetchData();
    } catch (saveError) {
      console.error("Erreur enregistrement trombinoscope :", saveError);
      setError("Impossible d’enregistrer ce profil.");
    } finally {
      setSaving(false);
    }
  }, [closeForm, currentId, fetchData, form, showNotice]);

  const onToggleVisible = useCallback(
    async (id) => {
      if (!id) return;

      try {
        setActionId(`visible-${id}`);
        setError("");

        await toggleTrombinoVisible(id);
        showNotice("Visibilité mise à jour.");
        await fetchData();
      } catch (toggleError) {
        console.error("Erreur changement visibilité :", toggleError);
        setError("Impossible de modifier la visibilité.");
      } finally {
        setActionId(null);
      }
    },
    [fetchData, showNotice],
  );

  const onToggleElected = useCallback(
    async (id) => {
      if (!id) return;

      try {
        setActionId(`elected-${id}`);
        setError("");

        await toggleTrombinoElected(id);
        showNotice("Type de profil mis à jour.");
        await fetchData();
      } catch (toggleError) {
        console.error("Erreur changement élu/personnel :", toggleError);
        setError("Impossible de modifier le type de profil.");
      } finally {
        setActionId(null);
      }
    },
    [fetchData, showNotice],
  );

  const openPreview = useCallback(
    async (id) => {
      if (!id) return;

      const localItem = items.find((item) => item.id === id);

      if (localItem) {
        setPreviewItem(localItem);
        return;
      }

      try {
        setActionId(`preview-${id}`);

        const row = await getTrombinoById(id);

        if (row) {
          setPreviewItem(row);
        }
      } catch (previewError) {
        console.error("Erreur aperçu trombinoscope :", previewError);
        setError("Impossible d’afficher l’aperçu de ce profil.");
      } finally {
        setActionId(null);
      }
    },
    [items],
  );

  const closePreview = useCallback(() => {
    setPreviewItem(null);
  }, []);

  const openDelete = useCallback(
    (id) => {
      const localItem = items.find((item) => item.id === id);

      setDeleteItem(
        localItem || {
          id,
          nom: "Ce profil",
          prenom: "",
        },
      );
    },
    [items],
  );

  const closeDelete = useCallback(() => {
    setDeleteItem(null);
  }, []);

  const onDelete = useCallback(async () => {
    if (!deleteItem?.id) return;

    try {
      setDeleting(true);
      setError("");

      await deleteTrombino(deleteItem.id);

      setDeleteItem(null);
      showNotice("Profil supprimé.");
      await fetchData();
    } catch (deleteError) {
      console.error("Erreur suppression trombinoscope :", deleteError);
      setError("Impossible de supprimer ce profil.");
    } finally {
      setDeleting(false);
    }
  }, [deleteItem, fetchData, showNotice]);

  const openMediaPicker = useCallback(() => {
    setMediaPickerOpen(true);
  }, []);

  const closeMediaPicker = useCallback(() => {
    setMediaPickerOpen(false);
  }, []);

  const handleSelectMedia = useCallback(
    (media) => {
      if (!media?.url) return;

      const absoluteUrl = toAbsolutePublicUrl(media.url);

      updateFormField("photo_url", absoluteUrl);
      setMediaPickerOpen(false);
    },
    [updateFormField],
  );

  return {
    q,
    setQ,
    electedFilter,
    visibleFilter,
    serviceFilter,
    page,
    setPage,
    limit,
    items,
    total,
    totalPages,
    stats,
    servicesOptions,

    loading,
    saving,
    deleting,
    actionId,
    error,
    notice,

    formOpen,
    currentId,
    form,
    fieldErrors,
    previewItem,
    deleteItem,
    mediaPickerOpen,

    fetchData,
    onSubmitSearch,
    changeElectedFilter,
    changeVisibleFilter,
    changeServiceFilter,
    changeLimit,
    resetFilters,

    openCreate,
    openEdit,
    openEditFromPreview,
    closeForm,
    updateFormField,
    onSave,

    onToggleVisible,
    onToggleElected,

    openPreview,
    closePreview,

    openDelete,
    closeDelete,
    onDelete,

    openMediaPicker,
    closeMediaPicker,
    handleSelectMedia,
  };
}
